import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Questions
  generateQuestion: async (questionType, level, topic, subtopic) => {
    const response = await axios.post(
      `${API}/questions/generate`,
      { question_type: questionType, level, topic, subtopic },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  saveFavorite: async (questionId, questionData) => {
    const response = await axios.post(
      `${API}/questions/favorite`,
      { question_id: questionId, question_data: questionData },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getFavorites: async () => {
    const response = await axios.get(`${API}/questions/favorites`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteFavorite: async (favoriteId) => {
    const response = await axios.delete(`${API}/questions/favorites/${favoriteId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Progress
  getProgress: async () => {
    const response = await axios.get(`${API}/progress`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  recordProgress: async (questionType, level, isCorrect, subtopic) => {
    const response = await axios.post(
      `${API}/progress/record`,
      { question_type: questionType, level, is_correct: isCorrect, subtopic },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Subscription
  getSubscriptionStatus: async () => {
    const response = await axios.get(`${API}/subscription/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getQuota: async (questionType, level) => {
    const response = await axios.get(
      `${API}/subscription/quota/${questionType}/${level}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  createCheckout: async (level, originUrl) => {
    const response = await axios.post(
      `${API}/payments/checkout`,
      { level, origin_url: originUrl },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getPaymentStatus: async (sessionId) => {
    const response = await axios.get(`${API}/payments/status/${sessionId}`);
    return response.data;
  },

  // Streak
  getStreak: async () => {
    const response = await axios.get(`${API}/streak`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Password reset
  forgotPassword: async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, {
      email,
      origin_url: window.location.origin
    });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await axios.get(`${API}/auth/verify-reset-token`, {
      params: { token }
    });
    return response.data;
  },

  // Admin
  checkAdmin: async () => {
    const response = await axios.get(`${API}/admin/check`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  adminListUsers: async () => {
    const response = await axios.get(`${API}/admin/users`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  adminResetUserPassword: async (userId) => {
    const response = await axios.post(
      `${API}/admin/reset-user-password`,
      { user_id: userId, origin_url: window.location.origin },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  adminResetUserPractices: async (userId) => {
    const response = await axios.post(
      `${API}/admin/reset-user-practices`,
      { user_id: userId },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  adminDeleteUser: async (userId) => {
    const response = await axios.delete(
      `${API}/admin/users/${userId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // MCQ Agent (draft generation & approval)
  adminMcqAgentGenerate: async ({ level, count, question_types }) => {
    const response = await axios.post(
      `${API}/admin/mcq-agent/generate`,
      { level, count, question_types },
      { headers: getAuthHeader(), timeout: 180000 }
    );
    return response.data;
  },

  adminListDrafts: async () => {
    const response = await axios.get(`${API}/admin/questions/drafts`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  adminApproveDraft: async (questionId) => {
    const response = await axios.post(
      `${API}/admin/questions/${questionId}/approve`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  adminRejectDraft: async (questionId) => {
    const response = await axios.post(
      `${API}/admin/questions/${questionId}/reject`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Weighted Assessment (WA)
  waStart: async () => {
    const response = await axios.post(`${API}/wa/start`, {}, {
      headers: getAuthHeader(),
      timeout: 180000,
    });
    return response.data;
  },
  waGet: async (id) => {
    const response = await axios.get(`${API}/wa/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  waMine: async () => {
    const response = await axios.get(`${API}/wa/mine`, { headers: getAuthHeader() });
    return response.data;
  },
  waSave: async (id, answers) => {
    const response = await axios.post(`${API}/wa/${id}/save`, { answers }, {
      headers: getAuthHeader(),
    });
    return response.data;
  },
  waSubmit: async (id, answers) => {
    const response = await axios.post(`${API}/wa/${id}/submit`, { answers }, {
      headers: getAuthHeader(),
      timeout: 120000,
    });
    return response.data;
  },

  // Admin - Role management
  adminUpdateRole: async (userId, role) => {
    const response = await axios.post(
      `${API}/admin/users/role`,
      { user_id: userId, role },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Admin - Custom questions
  listCustomQuestions: async (questionType, level) => {
    const params = {};
    if (questionType) params.question_type = questionType;
    if (level) params.level = level;
    const response = await axios.get(`${API}/admin/questions`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  },

  createCustomQuestion: async (question) => {
    const response = await axios.post(`${API}/admin/questions`, question, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateCustomQuestion: async (id, updates) => {
    const response = await axios.patch(`${API}/admin/questions/${id}`, updates, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteCustomQuestion: async (id) => {
    const response = await axios.delete(`${API}/admin/questions/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  downloadCsvTemplate: async () => {
    const response = await axios.get(`${API}/admin/questions/template`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  bulkUploadQuestions: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/admin/questions/bulk`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Printable Worksheets + OCR
  worksheetTopics: async () => {
    const response = await axios.get(`${API}/worksheets/topics`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  worksheetGenerate: async (questionType, level) => {
    const response = await axios.post(
      `${API}/worksheets/generate`,
      { question_type: questionType, level },
      { headers: getAuthHeader(), timeout: 120000 }
    );
    return response.data;
  },

  worksheetGet: async (id) => {
    const response = await axios.get(`${API}/worksheets/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  worksheetMine: async () => {
    const response = await axios.get(`${API}/worksheets/mine`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  worksheetGradePhoto: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/worksheets/${id}/grade-photo`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      },
      timeout: 90000
    });
    return response.data;
  },

  worksheetGradeManual: async (id, answers) => {
    const response = await axios.post(
      `${API}/worksheets/${id}/grade-manual`,
      { answers },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Oral Reading Practice
  oralListActive: async () => {
    const response = await axios.get(`${API}/oral-passages`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  oralPdfUrl: (passageId) => {
    const token = localStorage.getItem('token');
    return `${API}/oral-passages/${passageId}/pdf?token=${encodeURIComponent(token || '')}`;
  },

  oralAudioUrl: (passageId) => {
    const token = localStorage.getItem('token');
    return `${API}/oral-passages/${passageId}/audio?token=${encodeURIComponent(token || '')}`;
  },

  oralSubmitAttempt: async (passageId, audioBlob, durationSec) => {
    const formData = new FormData();
    const ext = (audioBlob.type || '').includes('mp4') ? 'm4a'
      : (audioBlob.type || '').includes('webm') ? 'webm'
      : (audioBlob.type || '').includes('wav') ? 'wav'
      : (audioBlob.type || '').includes('ogg') ? 'webm'
      : 'webm';
    formData.append('audio', audioBlob, `recording.${ext}`);
    if (durationSec != null) formData.append('duration_sec', String(durationSec));
    const response = await axios.post(`${API}/oral-passages/${passageId}/attempt`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      },
      timeout: 180000
    });
    return response.data;
  },

  oralMyAttempts: async () => {
    const response = await axios.get(`${API}/oral-passages/attempts/mine`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Admin - Oral passages
  adminListOralPassages: async () => {
    const response = await axios.get(`${API}/admin/oral-passages`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  adminCreateOralPassage: async ({ title, level, passage_text, active, pdf, audio }) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('level', level);
    formData.append('passage_text', passage_text);
    formData.append('active', active ? 'true' : 'false');
    formData.append('pdf', pdf);
    formData.append('audio', audio);
    const response = await axios.post(`${API}/admin/oral-passages`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      },
      timeout: 180000
    });
    return response.data;
  },

  adminUpdateOralPassage: async (id, updates) => {
    const response = await axios.patch(`${API}/admin/oral-passages/${id}`, updates, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  adminDeleteOralPassage: async (id) => {
    const response = await axios.delete(`${API}/admin/oral-passages/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ============ Tutors' Treasure Box (Marketplace) ============
  registerTutor: async (payload) => {
    const response = await axios.post(`${API}/auth/register-tutor`, payload);
    return response.data;
  },
  tutorMe: async () => {
    const response = await axios.get(`${API}/tutor/me`, { headers: getAuthHeader() });
    return response.data;
  },
  tutorStripeOnboard: async (originUrl) => {
    const response = await axios.post(`${API}/tutor/stripe/onboard`,
      { origin_url: originUrl }, { headers: getAuthHeader() });
    return response.data;
  },
  tutorStripeStatus: async () => {
    const response = await axios.get(`${API}/tutor/stripe/status`, { headers: getAuthHeader() });
    return response.data;
  },
  tutorStripeDashboardLink: async () => {
    const response = await axios.get(`${API}/tutor/stripe/dashboard-link`, { headers: getAuthHeader() });
    return response.data;
  },
  tutorSales: async () => {
    const response = await axios.get(`${API}/tutor/sales`, { headers: getAuthHeader() });
    return response.data;
  },
  tutorMyWorksheets: async () => {
    const response = await axios.get(`${API}/marketplace/worksheets/mine`, { headers: getAuthHeader() });
    return response.data;
  },
  tutorUploadWorksheet: async ({ title, description, level, price_cents, num_questions, answer_letters, pdf, preview_pdf }) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description || '');
    formData.append('level', level);
    formData.append('price_cents', String(price_cents));
    formData.append('num_questions', String(num_questions));
    formData.append('answer_letters', answer_letters);
    formData.append('pdf', pdf);
    if (preview_pdf) formData.append('preview_pdf', preview_pdf);
    const response = await axios.post(`${API}/marketplace/worksheets`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      timeout: 180000
    });
    return response.data;
  },
  tutorDeleteWorksheet: async (id) => {
    const response = await axios.delete(`${API}/marketplace/worksheets/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  adminListMarketplace: async (status) => {
    const url = status ? `${API}/admin/marketplace/worksheets?status=${status}` : `${API}/admin/marketplace/worksheets`;
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },
  adminApproveMarketplace: async (id) => {
    const response = await axios.post(`${API}/admin/marketplace/worksheets/${id}/approve`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  adminRejectMarketplace: async (id, reason) => {
    const response = await axios.post(`${API}/admin/marketplace/worksheets/${id}/reject`, { reason }, { headers: getAuthHeader() });
    return response.data;
  },
  marketplaceList: async (level) => {
    const url = level ? `${API}/marketplace/worksheets?level=${level}` : `${API}/marketplace/worksheets`;
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },
  marketplaceGet: async (id) => {
    const response = await axios.get(`${API}/marketplace/worksheets/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  marketplaceCheckout: async (worksheetId, originUrl) => {
    const response = await axios.post(`${API}/marketplace/checkout`,
      { worksheet_id: worksheetId, origin_url: originUrl }, { headers: getAuthHeader() });
    return response.data;
  },
  marketplaceCheckoutStatus: async (sessionId) => {
    const response = await axios.get(`${API}/marketplace/checkout/status/${sessionId}`);
    return response.data;
  },
  marketplaceMyPurchases: async () => {
    const response = await axios.get(`${API}/marketplace/purchases/mine`, { headers: getAuthHeader() });
    return response.data;
  },
  marketplaceGrade: async (purchaseId, answers) => {
    const response = await axios.post(`${API}/marketplace/purchases/${purchaseId}/grade`,
      { answers }, { headers: getAuthHeader() });
    return response.data;
  },
  marketplaceGradePhoto: async (purchaseId, imageFile) => {
    const formData = new FormData();
    formData.append('photo', imageFile);
    const response = await axios.post(`${API}/marketplace/purchases/${purchaseId}/grade-photo`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
    return response.data;
  },
  marketplaceMyGrades: async (purchaseId) => {
    const response = await axios.get(`${API}/marketplace/purchases/${purchaseId}/grades`, { headers: getAuthHeader() });
    return response.data;
  },

  // Diagnostic Package
  diagnosticGenerate: async () => {
    const response = await axios.post(`${API}/diagnostic/generate`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  diagnosticListSessions: async () => {
    const response = await axios.get(`${API}/diagnostic/sessions`, { headers: getAuthHeader() });
    return response.data;
  },
  diagnosticGetSession: async (sessionId) => {
    const response = await axios.get(`${API}/diagnostic/sessions/${sessionId}`, { headers: getAuthHeader() });
    return response.data;
  },
  diagnosticSubmit: async (sessionId, answers) => {
    const response = await axios.post(`${API}/diagnostic/sessions/${sessionId}/submit`,
      { answers }, { headers: getAuthHeader() });
    return response.data;
  },
};